import { createSlice } from "@reduxjs/toolkit";
import { getTests, getLabConfig } from "../utils/api";

// These will be set after the slice is created
let testsActions = {};

// Create the slice and assign actions after getting DTCPaging
export const createTestsReducer = async (labid) => {
  const DTCPaging = await getLabConfig("DTCPAGING");

  const initialState = {
    page: 1,
    tests: [],
    totalTests: 0,
    testsPerPage: DTCPaging,
    sort: "ALPHAUP",
    search: "",
    categories: [],
    categorydesc: "",
    categoryname: "",
    subcat: [],
    isLoading: false,
    hasError: false,
  };

  const testsSlice = createSlice({
    name: "tests",
    initialState,
    reducers: {
      setLoading(state) {
        state.isLoading = true;
      },
      init(state, action) {
        state.tests = action.payload.tests.tests;
        state.subcat = [];
        state.page = 1;
        state.categories = [];
        state.categorydesc = action.payload.categorydesc;
        state.categoryname = action.payload.categoryname;
        state.totalTests = action.payload.tests.totalTests;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      current(state, action) {
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      next(state, action) {
        state.page++;
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      prev(state, action) {
        state.page--;
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      gotopage(state, action) {
        state.page = action.payload.newpage;
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      sort(state, action) {
        state.page = 1;
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.sort = action.payload.sortBy;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      setSearch(state, action) {
        state.search = action.payload;
      },
      clearCategories(state) {
        state.categories = [];
      },
      search(state, action) {
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.search = action.payload.searchTerm;
        state.categorydesc = action.payload.catdesc;
        state.categoryname = action.payload.catname;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
      filter(state, action) {
        state.page = 1;
        state.tests = action.payload.tests.tests;
        state.totalTests = action.payload.tests.totalTests;
        state.categories = action.payload.categories;
        state.categorydesc = action.payload.categorydesc;
        state.categoryname = action.payload.categoryname;
        state.subcat = action.payload.subcat;
        state.isLoading = false;
        state.hasError = "error" in action.payload.tests;
      },
    },
  });

  // Assign actions so they're accessible outside
  testsActions = testsSlice.actions;

  return testsSlice.reducer;
};

// 🔁 Exported getter for actions
export const getTestsActions = () => testsActions;
export const setIsLoading = () => testsActions.setLoading;

// === Thunks ===

export const initTestsThunk = (catN, catD, overrideSearchTerm = null) => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    const searchterm = overrideSearchTerm !== null ? overrideSearchTerm : state.search;
    const tests = await getTests(1, state.testsPerPage, state.sort, searchterm, state.categories);
    dispatch(getTestsActions().init({ tests, categorydesc: catD, categoryname: catN }));
  };
};

export const currentTestsThunk = (page) => {
  const currentPage = page || 1;
  return async (dispatch, getState) => {
    const state = getState().tests;
    const tests = await getTests(currentPage, state.testsPerPage, state.sort, state.search, state.categories);
    dispatch(getTestsActions().current({ tests }));
  };
};

export const nextTestsThunk = () => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    if (state.page < Math.ceil(state.totalTests / state.testsPerPage)) {
      const tests = await getTests(state.page + 1, state.testsPerPage, state.sort, state.search, state.categories);
      dispatch(getTestsActions().next({ tests }));
    }
  };
};

export const prevTestsThunk = () => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    if (state.page > 1) {
      const tests = await getTests(state.page - 1, state.testsPerPage, state.sort, state.search, state.categories);
      dispatch(getTestsActions().prev({ tests }));
    }
  };
};

export const gotoTestsThunk = (newpage) => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    if (newpage <= Math.ceil(state.totalTests / state.testsPerPage)) {
      const tests = await getTests(newpage, state.testsPerPage, state.sort, state.search, state.categories);
      dispatch(getTestsActions().gotopage({ tests, newpage }));
    }
  };
};

export const sortTestsThunk = (sortBy) => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    const tests = await getTests(1, state.testsPerPage, sortBy, state.search, state.categories);
    dispatch(getTestsActions().sort({ tests, sortBy }));
  };
};

export const searchTestsThunk = (searchTerm, catname, catdesc) => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    const tests = await getTests(state.page, state.testsPerPage, state.sort, searchTerm);
    dispatch(getTestsActions().search({ tests, searchTerm, catname, catdesc }));
  };
};

export const filterTestsThunk = (category, single, catdesc, catname, subcat) => {
  return async (dispatch, getState) => {
    const state = getState().tests;
    let requestCategories = [...state.categories];
    let requestCatDesc = "";
    let requestCatName = "";
    let requestSubCat = [];

    if (single === 1) {
      requestCategories = [];
      requestCatDesc = catdesc;
      requestCatName = catname;
      requestSubCat = subcat;
    }

    if (requestCategories.includes(category)) {
      requestCategories = requestCategories.filter(c => c !== category);
    } else {
      requestCategories.push(category);
    }

    const tests = await getTests(1, state.testsPerPage, state.sort, '', requestCategories);
    dispatch(getTestsActions().filter({
      tests,
      categories: requestCategories,
      categorydesc: requestCatDesc,
      categoryname: requestCatName,
      subcat: requestSubCat
    }));
  };
};
